import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  IconLayers,
  IconCheckCircle,
  IconClock,
  IconCalendar,
  IconCalendarDays,
  IconSearch,
  IconSettings,
  IconSparkles,
  IconEdit,
  IconTrash
} from '../components/Icons';

const isVideoX3Brand = (brandName = '') => {
  const b = brandName.toLowerCase();
  return b.includes('genius') || b.includes('redito') || b.includes('uan') || b.includes('nexo') || b.includes('the brand') || b.includes('adcom');
};

const calculateBrandBilling = (brandName = '', totalUnits = 0) => {
  const b = brandName.toLowerCase();
  const UNIT_PRICE = 32000;

  if (b.includes('genius')) {
    const fee = 4000000;
    const included = 130;
    const extraUnits = Math.max(0, totalUnits - included);
    const extraAmount = extraUnits * UNIT_PRICE;
    return {
      planName: 'Fee Mensual (130 piezas)',
      fee,
      included,
      extraUnits,
      extraAmount,
      total: fee + extraAmount
    };
  }

  if (b.includes('edwcar')) {
    const fee = 3570000;
    const included = 10;
    const extraUnits = Math.max(0, totalUnits - included);
    const extraAmount = extraUnits * UNIT_PRICE;
    return {
      planName: 'Fee Mensual (10 publicaciones)',
      fee,
      included,
      extraUnits,
      extraAmount,
      total: fee + extraAmount
    };
  }

  if (b.includes('espacio vital')) {
    const fee = 15000000;
    const included = 60;
    const extraUnits = Math.max(0, totalUnits - included);
    const extraAmount = extraUnits * UNIT_PRICE;
    return {
      planName: 'Fee Estándar (60 piezas)',
      fee,
      included,
      extraUnits,
      extraAmount,
      total: fee + extraAmount
    };
  }

  return {
    planName: 'Por Pieza ($32.000 / unid)',
    fee: 0,
    included: 0,
    extraUnits: totalUnits,
    extraAmount: totalUnits * UNIT_PRICE,
    total: totalUnits * UNIT_PRICE
  };
};

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const STATUS_OPTS = [
  { v: 'REVISION_INTERNA', l: 'En revisión' },
  { v: 'REVISION_CLIENTE', l: 'Rev. cliente' },
  { v: 'APROBADO', l: 'Aprobado' },
  { v: 'SUBIDO', l: 'Entregado' }
];
const STATUS_COLORS: Record<string, string> = {
  REVISION_INTERNA: '#8b5cf6',
  REVISION_CLIENTE: '#f59e0b',
  APROBADO: '#10b981',
  SUBIDO: '#0070f3'
};
const TYPE_OPTS = [
  { v: 'ESTATICA', l: 'Estática' },
  { v: 'ANIMACION', l: 'Animación' },
  { v: 'VIDEO', l: 'Video' },
  { v: 'CARRUSEL', l: 'Carrusel' }
];
const CONTENT_OPTS = [
  { v: 'PARRILLA', l: 'Parrilla' },
  { v: 'PLAN_MEDIOS', l: 'Plan de medios' },
  { v: 'ESPORADICO', l: 'Esporádico' }
];
const FIELD_LABELS: Record<string, string> = {
  title: 'Asignación',
  type: 'Tipo de pieza',
  subBrandId: 'Línea creativa',
  executionDate: 'Fecha ejecución',
  dueDate: 'Fecha límite',
  publishDate: 'Fecha publicación',
  status: 'Estado',
  changeRounds: 'Rondas',
  contentType: 'Tipo contenido'
};

const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const toInput = (d: any) => d ? new Date(d).toISOString().split('T')[0] : '';

// ── COMPONENTE DE ANIMACIÓN RULETA DE NÚMEROS (ODOMETER COUNTER) ──
const RouletteNumber: React.FC<{ value: number | string; duration?: number }> = ({ value, duration = 1200 }) => {
  const target = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
  const [display, setDisplay] = useState<number>(0);

  useEffect(() => {
    if (target === 0) {
      const frames = [6, 3, 1, 0];
      let idx = 0;
      const interval = setInterval(() => {
        if (idx < frames.length) {
          setDisplay(frames[idx]);
          idx++;
        } else {
          clearInterval(interval);
          setDisplay(0);
        }
      }, 70);
      return () => clearInterval(interval);
    }

    let start: number | null = null;
    let animId: number;

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const currentVal = Math.round(ease * target);
      setDisplay(currentVal);

      if (progress < 1) {
        animId = requestAnimationFrame(animate);
      } else {
        setDisplay(target);
      }
    };

    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [target, duration]);

  return <span>{display}</span>;
};

// ── ICONO ANIMADO DE BARRAS DE ESTADÍSTICA (ON-THE-FLY BAR BOUNCE) ──
const AnimatedBarChartIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <line x1="18" y1="20" x2="18" y2="10" className="chart-bar-1" />
    <line x1="12" y1="20" x2="12" y2="4" className="chart-bar-2" />
    <line x1="6" y1="20" x2="6" y2="14" className="chart-bar-3" />
  </svg>
);

export const Production: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'ADMIN') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const now = new Date();
  const [brands, setBrands] = useState<any[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [pieces, setPieces] = useState<any[]>([]);
  const [materialsBoards, setMaterialsBoards] = useState<any[]>([]);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);
  const [editCell, setEditCell] = useState<{ pieceId: string; field: string } | null>(null);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyPiece, setHistoryPiece] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingData, setBillingData] = useState<any>(null);
  const [loadingBilling, setLoadingBilling] = useState(false);

  // Dropdowns Glass
  const [openMonthDropdown, setOpenMonthDropdown] = useState(false);
  const [openYearDropdown, setOpenYearDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenMonthDropdown(false);
        setOpenYearDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openBillingSummary = async () => {
    setShowBillingModal(true);
    setLoadingBilling(true);
    try {
      const r = await api.get('/production/billing-summary', { params: { month, year } });
      setBillingData(r.data);
    } catch {
      toast.error('Error al cargar informe de pagos');
    } finally {
      setLoadingBilling(false);
    }
  };

  const handlePrintFormalPDF = () => {
    if (!billingData) {
      toast.error('No hay datos disponibles para generar el informe.');
      return;
    }

    const monthName = MONTHS[month - 1] || 'Mes';
    const reportCode = `ZOIK-LQ-${year}-${String(month).padStart(2, '0')}-001`;
    const issueDate = new Date().toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const brandsList = billingData.brands || [];
    let totalPiecesSum = 0;
    let totalUnitsSum = 0;
    let totalApprovedSum = 0;
    let totalFeeBaseSum = 0;
    let totalExtrasSum = 0;

    brandsList.forEach((b: any) => {
      totalPiecesSum += (b.totalPieces || 0);
      totalUnitsSum += (b.totalUnits || 0);
      totalApprovedSum += (b.approvedUnits || 0);
      totalFeeBaseSum += (b.financial?.feeBase || 0);
      totalExtrasSum += (b.financial?.extraAmount || 0);
    });

    const grandTotal = billingData.grandTotalAmount || 0;
    const approvalRate = totalUnitsSum > 0 ? Math.round((totalApprovedSum / totalUnitsSum) * 100) : 0;

    const rowsHtml = brandsList.map((b: any, idx: number) => {
      const f = b.financial || {};
      const num = String(idx + 1).padStart(2, '0');
      const extraText = f.extraUnits > 0
        ? `<span style="color:#b45309;font-weight:700;">+${f.extraUnits} ($${(f.extraAmount || 0).toLocaleString('es-CO')})</span>`
        : `<span style="color:#94a3b8;">—</span>`;

      return `
        <tr>
          <td style="text-align:center;font-weight:700;color:#64748b;">${num}</td>
          <td style="font-weight:700;color:#0f2a4a;">
            ${b.brandName}
            ${b.isX3 ? '<span class="tag-x3">Videos x3</span>' : ''}
          </td>
          <td style="text-align:center;color:#475569;">${b.totalPieces || 0}</td>
          <td style="text-align:center;font-weight:800;color:#0f2a4a;">${b.totalUnits || 0}</td>
          <td style="text-align:center;font-weight:700;color:#059669;">${b.approvedUnits || 0}</td>
          <td style="color:#334155;font-size:9.5px;">${f.planName || 'Por Pieza'}</td>
          <td style="text-align:right;font-weight:600;">$${(f.feeBase || 0).toLocaleString('es-CO')}</td>
          <td style="text-align:right;">${extraText}</td>
          <td style="text-align:right;font-weight:800;color:#0f2a4a;">$${(f.totalAmount || 0).toLocaleString('es-CO')} COP</td>
        </tr>
      `;
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Por favor permite abrir ventanas emergentes para generar el PDF');
      return;
    }

    const htmlContent = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Informe Oficial de Liquidación - ${monthName} ${year} - ZOIK Agency</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    @page {
      size: letter portrait;
      margin: 10mm 12mm 12mm 12mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 0;
      background: #f8fafc;
      color: #0f172a;
      line-height: 1.35;
      font-size: 10px;
    }
    @media screen {
      body {
        padding: 24px 16px;
        background: #0f172a;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .screen-toolbar {
        max-width: 880px;
        width: 100%;
        background: #1e293b;
        color: white;
        padding: 12px 20px;
        border-radius: 12px;
        margin-bottom: 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 10px 25px rgba(0,0,0,0.4);
      }
      .print-btn {
        background: #0070f3;
        color: white;
        border: none;
        padding: 9px 20px;
        border-radius: 8px;
        font-weight: 800;
        font-size: 13px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: 'Montserrat', sans-serif;
        box-shadow: 0 4px 14px rgba(0, 112, 243, 0.4);
        transition: transform 0.15s ease, background 0.15s ease;
      }
      .print-btn:hover {
        background: #005bb5;
        transform: translateY(-1px);
      }
      .document-container {
        max-width: 880px;
        width: 100%;
        background: white;
        padding: 36px 40px;
        border-radius: 10px;
        box-shadow: 0 15px 40px rgba(0,0,0,0.3);
      }
    }
    @media print {
      body {
        background: white;
        padding: 0;
      }
      .screen-toolbar {
        display: none !important;
      }
      .document-container {
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        box-shadow: none !important;
        border: none !important;
      }
    }

    h1, h2, h3, h4, .brand-font {
      font-family: 'Montserrat', sans-serif;
    }
    .header-layout {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 18px;
      padding-bottom: 14px;
      border-bottom: 2px solid #0f2a4a;
    }
    .brand-section {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .brand-logo {
      width: 48px;
      height: 48px;
      background: linear-gradient(135deg, #0f2a4a 0%, #0070f3 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 900;
      font-size: 22px;
      font-family: 'Montserrat', sans-serif;
      box-shadow: 0 4px 10px rgba(15, 42, 74, 0.2);
    }
    .brand-name {
      font-size: 20px;
      font-weight: 900;
      color: #0f2a4a;
      letter-spacing: -0.5px;
      margin: 0;
      line-height: 1.1;
    }
    .brand-sub {
      font-size: 9.5px;
      font-weight: 600;
      color: #64748b;
      margin: 3px 0 0;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .meta-card {
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 8px 14px;
      text-align: right;
      min-width: 250px;
    }
    .meta-title {
      font-size: 10.5px;
      font-weight: 900;
      color: #0f2a4a;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin: 0;
    }
    .meta-line {
      font-size: 9px;
      color: #475569;
      margin-top: 2px;
    }
    .meta-badge {
      display: inline-block;
      background: #0f2a4a;
      color: #ffffff;
      font-size: 8.5px;
      font-weight: 800;
      padding: 2px 8px;
      border-radius: 4px;
      margin-top: 4px;
      letter-spacing: 0.4px;
    }

    /* Executive summary cards */
    .summary-grid {
      display: grid;
      grid-template-columns: 1.4fr 1fr 1fr 1fr;
      gap: 10px;
      margin-bottom: 20px;
    }
    .summary-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px 12px;
      border-top: 3px solid #0f2a4a;
    }
    .summary-card.highlight {
      background: linear-gradient(135deg, #0f2a4a 0%, #1e3a8a 100%);
      border: none;
      color: white;
    }
    .summary-label {
      font-size: 8.5px;
      font-weight: 800;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }
    .summary-card.highlight .summary-label {
      color: rgba(255,255,255,0.85);
    }
    .summary-val {
      font-size: 19px;
      font-weight: 900;
      color: #0f2a4a;
      margin-top: 3px;
      font-family: 'Montserrat', sans-serif;
      line-height: 1.1;
    }
    .summary-card.highlight .summary-val {
      color: #ffffff;
    }
    .summary-subtext {
      font-size: 8.5px;
      color: #64748b;
      margin-top: 2px;
      font-weight: 600;
    }
    .summary-card.highlight .summary-subtext {
      color: rgba(255,255,255,0.8);
    }

    /* Financial Table */
    .table-header-title {
      font-size: 11px;
      font-weight: 800;
      color: #0f2a4a;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin: 0 0 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .fin-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9.5px;
      margin-bottom: 20px;
    }
    .fin-table th {
      background: #0f2a4a;
      color: #ffffff;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      padding: 7px 8px;
      border: 1px solid #0f2a4a;
      font-size: 8.5px;
      font-family: 'Montserrat', sans-serif;
    }
    .fin-table td {
      padding: 6.5px 8px;
      border: 1px solid #e2e8f0;
      color: #1e293b;
    }
    .fin-table tr:nth-child(even) {
      background: #f8fafc;
    }
    .fin-table .row-total {
      background: #f1f5f9 !important;
      font-weight: 900;
      border-top: 2px solid #0f2a4a;
      border-bottom: 2px solid #0f2a4a;
    }
    .fin-table .row-total td {
      color: #0f2a4a;
      font-size: 10px;
      padding: 8px 8px;
    }
    .tag-x3 {
      background: #fef3c7;
      color: #b45309;
      font-size: 7.5px;
      font-weight: 800;
      padding: 1px 4px;
      border-radius: 3px;
      margin-left: 4px;
      display: inline-block;
    }

    /* Legal Notes */
    .notes-card {
      background: #fafafa;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px 14px;
      margin-bottom: 22px;
      font-size: 8.5px;
      color: #475569;
      line-height: 1.45;
    }
    .notes-title {
      font-size: 9.5px;
      font-weight: 800;
      color: #0f2a4a;
      text-transform: uppercase;
      margin: 0 0 5px;
      letter-spacing: 0.4px;
    }

    /* Signatures */
    .signatures-row {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 20px;
      margin-top: 30px;
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    .signature-item {
      text-align: center;
    }
    .signature-line {
      border-top: 1.5px solid #0f2a4a;
      margin-top: 38px;
      margin-bottom: 5px;
    }
    .sign-title {
      font-size: 9px;
      font-weight: 800;
      color: #0f2a4a;
      text-transform: uppercase;
      font-family: 'Montserrat', sans-serif;
    }
    .sign-subtitle {
      font-size: 8.5px;
      color: #64748b;
      margin-top: 1px;
    }

    /* Footer */
    .footer-bar {
      border-top: 1px solid #e2e8f0;
      padding-top: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 8px;
      color: #94a3b8;
      font-weight: 600;
    }
  </style>
</head>
<body>
  <div class="screen-toolbar">
    <div style="display:flex;align-items:center;gap:10px;">
      <span style="font-size:18px;">📄</span>
      <div>
        <div style="font-weight:800;font-size:14px;font-family:'Montserrat',sans-serif;">ZOIK Agency — Informe Oficial de Liquidación</div>
        <div style="font-size:11px;opacity:0.8;">${monthName} ${year} · Formato Ejecutivo A4/Carta</div>
      </div>
    </div>
    <button class="print-btn" onclick="window.print()">
      🖨️ Imprimir / Guardar como PDF
    </button>
  </div>

  <div class="document-container">
    <!-- Header -->
    <div class="header-layout">
      <div class="brand-section">
        <div class="brand-logo">Z</div>
        <div>
          <h1 class="brand-name">ZOIK AGENCY S.A.S.</h1>
          <p class="brand-sub">Dirección de Operaciones & Control Financiero</p>
          <p style="font-size:8.5px;color:#94a3b8;margin:2px 0 0;">NIT: 901.458.712-1 · Medellín, Colombia · soporte@zoikagency.com</p>
        </div>
      </div>
      <div class="meta-card">
        <h3 class="meta-title">Informe de Liquidación</h3>
        <div class="meta-line"><strong>Cód:</strong> ${reportCode}</div>
        <div class="meta-line"><strong>Período:</strong> ${monthName.toUpperCase()} ${year}</div>
        <div class="meta-line"><strong>Emisión:</strong> ${issueDate}</div>
        <div><span class="meta-badge">CONSOLIDADO OFICIAL AUDITADO</span></div>
      </div>
    </div>

    <!-- Executive Summary KPI Cards -->
    <div class="summary-grid">
      <div class="summary-card highlight">
        <div class="summary-label">Total a Facturar / Liquidar</div>
        <div class="summary-val">$${grandTotal.toLocaleString('es-CO')}</div>
        <div class="summary-subtext">Pesos Colombianos (COP)</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Volumen Producido</div>
        <div class="summary-val">${totalUnitsSum} <span style="font-size:12px;font-weight:600;">unids</span></div>
        <div class="summary-subtext">${totalPiecesSum} fichas registradas</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Unidades Aprobadas</div>
        <div class="summary-val" style="color:#059669;">${totalApprovedSum} <span style="font-size:12px;font-weight:600;">(${approvalRate}%)</span></div>
        <div class="summary-subtext">Certificadas para entrega</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Marcas / Cuentas</div>
        <div class="summary-val">${brandsList.length}</div>
        <div class="summary-subtext">Consolidadas en el periodo</div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="table-header-title">
      <span>1. Desglose Financiero y Operativo por Marca</span>
      <span style="font-size:9px;color:#64748b;font-weight:600;text-transform:none;">Valores expresados en COP</span>
    </div>

    <table class="fin-table">
      <thead>
        <tr>
          <th style="width:30px;text-align:center;">N°</th>
          <th style="text-align:left;">Cliente / Marca</th>
          <th style="width:50px;text-align:center;">Fichas</th>
          <th style="width:65px;text-align:center;">Unids (Diaps)</th>
          <th style="width:65px;text-align:center;">Aprobadas</th>
          <th style="text-align:left;">Acuerdo / Modalidad</th>
          <th style="width:90px;text-align:right;">Fee Base</th>
          <th style="width:110px;text-align:right;">Excedentes / Extras</th>
          <th style="width:110px;text-align:right;">Total a Cobrar</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
        <tr class="row-total">
          <td colspan="2" style="text-align:left;letter-spacing:0.5px;text-transform:uppercase;">TOTALES CONSOLIDADOS</td>
          <td style="text-align:center;">${totalPiecesSum}</td>
          <td style="text-align:center;">${totalUnitsSum}</td>
          <td style="text-align:center;color:#059669;">${totalApprovedSum}</td>
          <td style="color:#64748b;font-size:8.5px;">Tarifa base: $32.000 / unid</td>
          <td style="text-align:right;">$${totalFeeBaseSum.toLocaleString('es-CO')}</td>
          <td style="text-align:right;color:${totalExtrasSum > 0 ? '#b45309' : '#64748b'};">
            ${totalExtrasSum > 0 ? `$${totalExtrasSum.toLocaleString('es-CO')}` : '—'}
          </td>
          <td style="text-align:right;font-size:11px;color:#0f2a4a;">$${grandTotal.toLocaleString('es-CO')} COP</td>
        </tr>
      </tbody>
    </table>

    <!-- Legal Notes & Clauses -->
    <div class="notes-card">
      <div class="notes-title">2. Notas de Auditoría y Términos de Liquidación</div>
      <div style="margin-bottom:3px;"><strong>1. Cómputo de Unidades:</strong> Las piezas estáticas corresponden a 1 unidad. Los carruseles se liquidan según el número de diapositivas efectivas registradas. Las marcas bajo convenio multiplicador aplican factor x3 en piezas audiovisuales/animaciones.</div>
      <div style="margin-bottom:3px;"><strong>2. Políticas de Fee y Excedentes:</strong> Los contratos bajo modalidad de Fee mensual incluyen el cupo de unidades estipulado. Las unidades producidas por encima del cupo son facturadas como excedentes a la tarifa de $32.000 COP por unidad.</div>
      <div><strong>3. Validez y Soporte:</strong> El presente documento resume la liquidación operativa y sirve como anexo vinculante para la expedición de las facturas de venta y comprobantes de pago del periodo.</div>
    </div>

    <!-- Signatures -->
    <div class="signatures-row">
      <div class="signature-item">
        <div class="signature-line"></div>
        <div class="sign-title">Gerencia de Operaciones</div>
        <div class="sign-subtitle">ZOIK Agency · Producción Creativa</div>
      </div>
      <div class="signature-item">
        <div class="signature-line"></div>
        <div class="sign-title">Dirección Financiera</div>
        <div class="sign-subtitle">Auditoría & Control de Gestión</div>
      </div>
      <div class="signature-item">
        <div class="signature-line"></div>
        <div class="sign-title">Representación Legal</div>
        <div class="sign-subtitle">Aprobación Final de Liquidación</div>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer-bar">
      <div>ZOIK Platform v2.4 · Sistema Central de Liquidación y Analítica</div>
      <div>Documento Electrónico Certificado · Código de Verificación: ${reportCode}</div>
      <div>Página 1 de 1</div>
    </div>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 400);
    };
  <\/script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };


  const [newSubBrand, setNewSubBrand] = useState('');
  const [filterSub, setFilterSub] = useState('');
  const [filterText, setFilterText] = useState('');
  const inputRef = useRef<any>(null);
  const editValRef = useRef('');
  const setVal = (v: string) => { editValRef.current = v; setEditVal(v); };

  useEffect(() => { loadBrands(); }, []);
  useEffect(() => { if (selectedBrand) loadPieces(); }, [selectedBrand, month, year, filterSub]);
  useEffect(() => { if (editCell) setTimeout(() => inputRef.current?.focus(), 50); }, [editCell]);

  const loadBrands = async () => {
    try {
      const r = await api.get('/production/brands');
      const bList = Array.isArray(r.data) ? r.data : [];
      setBrands(bList);
      if (bList.length > 0) setSelectedBrand(bList[0]);
    } catch {}
    api.get('/processes/materials-boards').then(r => setMaterialsBoards(r.data)).catch(() => {});
  };

  const loadPieces = async () => {
    setLoading(true);
    try {
      const params: any = { month, year };
      if (filterSub) params.subBrandId = filterSub;
      const r = await api.get(`/production/brands/${selectedBrand.id}/pieces`, { params });
      setPieces(Array.isArray(r.data) ? r.data : []);
    } catch {} finally { setLoading(false); }
  };

  const startEdit = (pieceId: string, field: string, currentVal: any) => {
    if (user?.role === 'CLIENTE') return;
    let val = '';
    if (['executionDate', 'dueDate', 'publishDate'].includes(field)) val = toInput(currentVal);
    else val = currentVal != null ? String(currentVal) : '';
    setEditCell({ pieceId, field });
    setVal(val);
  };

  const commitEdit = async () => {
    if (!editCell) return;
    const val = editValRef.current;
    const { pieceId, field } = editCell;
    setEditCell(null);
    setSaving(true);
    try {
      const r = await api.put(`/production/pieces/${pieceId}`, { field, value: val });
      setPieces(prev => prev.map(p => p.id === pieceId ? { ...p, [field]: r.data[field] } : p));
      toast.success('Guardado');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Error al guardar');
      await loadPieces();
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => setEditCell(null);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  const openHistory = async (piece: any) => {
    setHistoryPiece(piece);
    try {
      const r = await api.get(`/production/pieces/${piece.id}/history`);
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch {}
  };

  const toggleTableType = async () => {
    if (!selectedBrand) return;
    const next = selectedBrand.tableType === 'CAMPAIGN' ? 'GENERAL' : 'CAMPAIGN';
    try {
      await api.put(`/production/brands/${selectedBrand.id}/tabletype`, { tableType: next });
      setSelectedBrand((p: any) => ({ ...p, tableType: next }));
      setBrands(prev => prev.map(b => b.id === selectedBrand.id ? { ...b, tableType: next } : b));
      toast.success('Tipo de tabla actualizado');
    } catch { toast.error('Error'); }
  };

  const addSubBrand = async () => {
    if (!newSubBrand.trim() || !selectedBrand) return;
    try {
      const r = await api.post('/production/subbrand', { name: newSubBrand.trim(), brandId: selectedBrand.id });
      setSelectedBrand((p: any) => ({ ...p, subBrands: [...(p.subBrands || []), r.data] }));
      setBrands(prev => prev.map(b => b.id === selectedBrand.id ? { ...b, subBrands: [...(b.subBrands || []), r.data] } : b));
      setNewSubBrand('');
      toast.success('Submarca añadida');
    } catch { toast.error('Error'); }
  };

  const deleteSubBrand = async (id: string) => {
    try {
      await api.delete(`/production/subbrand/${id}`);
      setSelectedBrand((p: any) => ({ ...p, subBrands: (p.subBrands || []).filter((s: any) => s.id !== id) }));
      setBrands(prev => prev.map(b => b.id === selectedBrand.id ? { ...b, subBrands: (b.subBrands || []).filter((s: any) => s.id !== id) } : b));
    } catch { toast.error('Error'); }
  };

  const addRow = async () => {
    if (!selectedBrand) return;
    try {
      const r = await api.post('/production/piece', { brandId: selectedBrand.id, subBrandId: filterSub || null });
      setPieces(prev => [...prev, r.data]);
      setEditCell({ pieceId: r.data.id, field: 'title' });
      setEditVal('Nueva pieza');
      toast.success('Fila añadida');
    } catch { toast.error('Error al agregar fila'); }
  };

  const deleteRow = async (pieceId: string) => {
    if (!window.confirm('¿Eliminar esta fila?')) return;
    try {
      await api.delete(`/production/piece/${pieceId}`);
      setPieces(prev => prev.filter(p => p.id !== pieceId));
      toast.success('Fila eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const isCampaign = selectedBrand?.tableType === 'CAMPAIGN';
  const filteredPieces = filterText.trim() === '' ? pieces : pieces.filter((p: any) => {
    const mbName = materialsBoards.find((b: any) => b.id === p.materialsBoardId)?.name?.toLowerCase() || '';
    const subName = (selectedBrand?.subBrands || []).find((s: any) => s.id === p.subBrandId)?.name?.toLowerCase() || '';
    const q = filterText.toLowerCase();
    return mbName.includes(q) || subName.includes(q);
  });
  const subBrands = selectedBrand?.subBrands || [];

  const getPieceSlides = (p: any) => {
    const bName = selectedBrand?.name || '';
    const isX3 = isVideoX3Brand(bName);
    const isVid = ['VIDEO', 'ANIMACION', 'REEL'].includes(p.type) || ['VIDEO', 'ANIMACION', 'REEL'].includes(p.contentType);
    let count = 1;
    if (p.type === 'CARRUSEL' && Array.isArray(p.files) && p.files.length > 0) {
      count = p.files.length;
    } else if (isVid) {
      count = isX3 ? 3 : 1;
    }
    return count;
  };

  const totalSlides = filteredPieces.reduce((acc: number, p: any) => acc + getPieceSlides(p), 0);
  const approvedSlides = filteredPieces.filter((p: any) => p.status === 'APROBADO' || p.status === 'SUBIDO').reduce((acc: number, p: any) => acc + getPieceSlides(p), 0);
  const pendingSlides = totalSlides - approvedSlides;

  const CellEdit = ({ pieceId, field, children, value, className }: any) => {
    const active = editCell?.pieceId === pieceId && editCell?.field === field;
    const isDate = ['executionDate', 'dueDate', 'publishDate'].includes(field);
    const isSelect = ['status', 'type', 'contentType', 'subBrandId'].includes(field);
    const isNum = field === 'changeRounds';

    if (active) {
      if (isSelect) {
        const opts = field === 'status' ? STATUS_OPTS : field === 'type' ? TYPE_OPTS : field === 'contentType' ? CONTENT_OPTS : subBrands.map((s: any) => ({ v: s.id, l: s.name }));
        return (
          <td style={{ ...tdStyle, padding: 0, background: 'rgba(254, 243, 199, 0.7)' }}>
            <select
              ref={inputRef}
              value={editVal}
              onChange={e => setVal(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleKeyDown}
              style={{
                width: '100%',
                height: '100%',
                border: '2px solid #0070f3',
                padding: '6px 8px',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: '"Montserrat", sans-serif',
                outline: 'none',
                background: 'white',
                color: '#0f2a4a',
                borderRadius: '6px'
              }}
            >
              <option value="">— Sin asignar —</option>
              {opts.map((o: any) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </td>
        );
      }
      return (
        <td style={{ ...tdStyle, padding: 0, background: 'rgba(254, 243, 199, 0.7)' }}>
          <input
            ref={inputRef}
            type={isDate ? 'date' : isNum ? 'number' : 'text'}
            value={editVal}
            onChange={e => {
              setVal(e.target.value);
              if (isDate && e.target.value) setTimeout(commitEdit, 0);
            }}
            onBlur={isDate ? undefined : commitEdit}
            onKeyDown={handleKeyDown}
            style={{
              width: '100%',
              height: '100%',
              border: '2px solid #0070f3',
              padding: '6px 8px',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: '"Montserrat", sans-serif',
              outline: 'none',
              background: 'white',
              color: '#0f2a4a',
              boxSizing: 'border-box',
              borderRadius: '6px'
            }}
          />
        </td>
      );
    }
    return (
      <td
        style={{ ...tdStyle, ...(user?.role !== 'CLIENTE' ? { cursor: 'cell' } : {}) }}
        className={className}
        onClick={() => user?.role !== 'CLIENTE' && startEdit(pieceId, field, value)}
        title={user?.role !== 'CLIENTE' ? 'Clic para editar' : ''}
      >
        {children}
      </td>
    );
  };

  const tdStyle: any = {
    padding: '9px 12px',
    borderRight: '1px solid rgba(15, 42, 74, 0.05)',
    borderBottom: '1px solid rgba(15, 42, 74, 0.05)',
    fontSize: '12px',
    color: '#0f2a4a',
    whiteSpace: 'nowrap',
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    position: 'relative'
  };

  const thStyle: any = {
    padding: '11px 12px',
    borderRight: '1px solid rgba(255, 255, 255, 0.12)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
    fontSize: '11px',
    fontWeight: 800,
    color: '#ffffff',
    background: 'rgba(15, 42, 74, 0.92)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    whiteSpace: 'nowrap',
    textAlign: 'left' as const,
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
    letterSpacing: '0.3px',
    textTransform: 'uppercase'
  };

  return (
    <div className="zoik-page-container" style={{
      display: 'flex', height: '100vh', maxHeight: '100vh', width: '100vw', maxWidth: '100vw', overflow: 'hidden',
      background: 'radial-gradient(at 10% 10%, rgba(224, 242, 254, 0.4) 0px, transparent 50%), radial-gradient(at 90% 90%, rgba(241, 245, 249, 0.7) 0px, transparent 50%), #f8fafc',
      fontFamily: '"Montserrat", sans-serif'
    }}>
      {/* ESTILOS GLOBALES DE LIQUID GLASS Y ANIMACIONES */}
      <style>{`
        @keyframes glassDeploy {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(-6px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .liquid-glass-card {
          background: rgba(255, 255, 255, 0.48);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.75);
          box-shadow: 0 8px 32px 0 rgba(15, 42, 74, 0.04), inset 0 1.5px 2px rgba(255, 255, 255, 0.9);
          transition: all 0.28s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .liquid-glass-card:hover {
          background: rgba(255, 255, 255, 0.68);
          transform: translateY(-3px);
          box-shadow: 0 16px 40px -4px rgba(15, 42, 74, 0.1), inset 0 1.5px 2px rgba(255, 255, 255, 0.95);
          border-color: rgba(0, 112, 243, 0.35);
        }
        .liquid-glass-panel {
          background: rgba(255, 255, 255, 0.65);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.85);
          box-shadow: 0 8px 32px 0 rgba(15, 42, 74, 0.05), inset 0 1.5px 2px rgba(255, 255, 255, 0.95);
        }
        .glass-window-popup {
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.95);
          box-shadow: 0 20px 45px -10px rgba(15, 42, 74, 0.18), 0 0 1px 1px rgba(255, 255, 255, 0.9), inset 0 1.5px 2px rgba(255, 255, 255, 0.95);
          animation: glassDeploy 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          transform-origin: top left;
          z-index: 9999;
        }
        .liquid-glass-input {
          background: rgba(255, 255, 255, 0.55);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border: 1px solid rgba(255, 255, 255, 0.85);
          box-shadow: 0 2px 8px rgba(15, 42, 74, 0.03), inset 0 1px 2px rgba(255, 255, 255, 0.85);
          transition: all 0.2s ease;
        }
        .liquid-glass-input:focus {
          border-color: rgba(0, 112, 243, 0.5);
          background: rgba(255, 255, 255, 0.85);
          box-shadow: 0 0 0 3px rgba(0, 112, 243, 0.12), inset 0 1px 2px rgba(255, 255, 255, 0.95);
        }
        @keyframes barWave1 {
          0%, 100% { transform: scaleY(0.45); }
          50% { transform: scaleY(1.05); }
        }
        @keyframes barWave2 {
          0%, 100% { transform: scaleY(1.05); }
          50% { transform: scaleY(0.35); }
        }
        @keyframes barWave3 {
          0%, 100% { transform: scaleY(0.65); }
          50% { transform: scaleY(1.15); }
        }
        .chart-bar-1 {
          animation: barWave1 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          transform-origin: center 20px;
        }
        .chart-bar-2 {
          animation: barWave2 1.3s cubic-bezier(0.4, 0, 0.2, 1) infinite 0.15s;
          transform-origin: center 20px;
        }
        .chart-bar-3 {
          animation: barWave3 1.0s cubic-bezier(0.4, 0, 0.2, 1) infinite 0.3s;
          transform-origin: center 20px;
        }
      `}</style>

      <Sidebar />
      <main className="zoik-page-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '24px 32px' }}>
        
        {/* Cabecera Principal */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexShrink: 0, position: 'relative', zIndex: 50 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 900, color: '#0f2a4a' }}>
              Producción
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(15, 42, 74, 0.5)', fontWeight: 500 }}>
              Control de piezas, entregables y liquidación
            </p>
          </div>

          {/* Controles de Cabecera: Mes, Año, Informe de Pagos y Config */}
          <div ref={dropdownRef} style={{ display: 'flex', gap: '10px', alignItems: 'center', position: 'relative' }}>
            
            {/* Desplegable Glass Mes */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => { setOpenMonthDropdown(!openMonthDropdown); setOpenYearDropdown(false); }}
                style={{
                  padding: '8px 14px',
                  borderRadius: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  background: openMonthDropdown ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#0f2a4a',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(15, 42, 74, 0.03), inset 0 1px 2px rgba(255, 255, 255, 0.85)',
                  transition: 'all 0.2s ease',
                  height: '38px',
                  boxSizing: 'border-box'
                }}
              >
                <IconCalendar size={14} color="#0070f3" />
                <span>{MONTHS[month - 1]}</span>
                <span style={{ fontSize: '9px', color: '#64748b', marginLeft: '2px' }}>▼</span>
              </button>
              {openMonthDropdown && (
                <div className="glass-window-popup" style={{ position: 'absolute', top: '44px', left: 0, width: '180px', maxHeight: '260px', overflowY: 'auto', borderRadius: '16px', padding: '6px' }}>
                  {MONTHS.map((mName, i) => {
                    const isSel = month === (i + 1);
                    return (
                      <button
                        key={mName}
                        type="button"
                        onClick={() => { setMonth(i + 1); setOpenMonthDropdown(false); }}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '10px',
                          border: 'none',
                          background: isSel ? 'rgba(0, 112, 243, 0.1)' : 'transparent',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: isSel ? 800 : 600,
                          color: isSel ? '#0070f3' : '#0f2a4a',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>{mName}</span>
                        {i + 1 === (now.getMonth() + 1) && <span style={{ fontSize: '10px', color: '#10b981', fontWeight: 800 }}>• Actual</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Desplegable Glass Año */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => { setOpenYearDropdown(!openYearDropdown); setOpenMonthDropdown(false); }}
                style={{
                  padding: '8px 14px',
                  borderRadius: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  background: openYearDropdown ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#0f2a4a',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(15, 42, 74, 0.03), inset 0 1px 2px rgba(255, 255, 255, 0.85)',
                  transition: 'all 0.2s ease',
                  height: '38px',
                  boxSizing: 'border-box'
                }}
              >
                <IconCalendarDays size={14} color="#0070f3" />
                <span>{year}</span>
                <span style={{ fontSize: '9px', color: '#64748b', marginLeft: '2px' }}>▼</span>
              </button>
              {openYearDropdown && (
                <div className="glass-window-popup" style={{ position: 'absolute', top: '44px', left: 0, width: '130px', borderRadius: '16px', padding: '6px' }}>
                  {[2025, 2026, 2027].map(y => {
                    const isSel = year === y;
                    return (
                      <button
                        key={y}
                        type="button"
                        onClick={() => { setYear(y); setOpenYearDropdown(false); }}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '10px',
                          border: 'none',
                          background: isSel ? 'rgba(0, 112, 243, 0.1)' : 'transparent',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: isSel ? 800 : 600,
                          color: isSel ? '#0070f3' : '#0f2a4a',
                          cursor: 'pointer'
                        }}
                      >
                        <span>{y}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Botón Informe de Pagos con Animación de Barras Estadísticas */}
            {user?.role === 'ADMIN' && (
              <button
                onClick={openBillingSummary}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 18px',
                  borderRadius: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.9))',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 0.35)',
                  height: '38px',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 18px rgba(16, 185, 129, 0.3)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(15, 42, 74, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 0.35)';
                }}
              >
                <AnimatedBarChartIcon size={16} />
                <span>Informe de Pagos</span>
              </button>
            )}

            {/* Botón Config */}
            {user?.role === 'ADMIN' && selectedBrand && (
              <button
                onClick={() => setShowSettings(s => !s)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  background: showSettings ? 'rgba(15, 42, 74, 0.9)' : 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  color: showSettings ? 'white' : '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  boxShadow: '0 2px 8px rgba(15, 42, 74, 0.03), inset 0 1px 2px rgba(255, 255, 255, 0.85)',
                  height: '38px',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  if (!showSettings) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  if (!showSettings) e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconSettings size={14} color={showSettings ? '#ffffff' : '#0f2a4a'} />
                <span>Config</span>
              </button>
            )}
          </div>
        </div>

        {/* Selección de Marcas con Animación (Azul seleccionada / Blanco translúcido no seleccionadas) */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexShrink: 0, overflowX: 'auto', paddingBottom: '4px', alignItems: 'center' }}>
          {brands.map(b => {
            const isSelected = selectedBrand?.id === b.id;
            return (
              <button
                key={b.id}
                onClick={() => { setSelectedBrand(b); setFilterSub(''); }}
                style={{
                  padding: '9px 18px',
                  borderRadius: '14px',
                  border: isSelected ? '1px solid rgba(255, 255, 255, 0.7)' : '1px solid rgba(255, 255, 255, 0.8)',
                  background: isSelected
                    ? 'linear-gradient(135deg, rgba(0, 112, 243, 0.95), rgba(0, 81, 179, 0.9))'
                    : 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  color: isSelected ? '#ffffff' : '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: isSelected ? 800 : 600,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  whiteSpace: 'nowrap',
                  boxShadow: isSelected
                    ? '0 2px 6px rgba(15, 42, 74, 0.08), inset 0 1px 2px rgba(255, 255, 255, 0.5)'
                    : '0 2px 8px rgba(15, 42, 74, 0.03), inset 0 1px 1.5px rgba(255, 255, 255, 0.85)',
                  transform: isSelected ? 'translateY(-1px)' : 'translateY(0)',
                  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={e => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.55)';
                  }
                }}
              >
                <span>{b.name}</span>
                {b.tableType === 'CAMPAIGN' && (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: 800,
                    background: isSelected ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 112, 243, 0.1)',
                    color: isSelected ? '#ffffff' : '#0070f3',
                    padding: '2px 6px',
                    borderRadius: '8px'
                  }}>
                    Camp.
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Panel de Configuración de Marca */}
        {showSettings && selectedBrand && user?.role === 'ADMIN' && (
          <div className="liquid-glass-panel" style={{ borderRadius: '18px', padding: '18px 22px', marginBottom: '18px', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.5)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                  Tipo de tabla
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['GENERAL', 'CAMPAIGN'].map(t => {
                    const isSel = selectedBrand.tableType === t;
                    return (
                      <button
                        key={t}
                        onClick={() => selectedBrand.tableType !== t && toggleTableType()}
                        style={{
                          padding: '7px 16px',
                          borderRadius: '12px',
                          border: isSel ? '1px solid rgba(255,255,255,0.7)' : '1px solid rgba(255,255,255,0.8)',
                          background: isSel ? 'linear-gradient(135deg, rgba(15, 42, 74, 0.95), rgba(15, 42, 74, 0.85))' : 'rgba(255, 255, 255, 0.5)',
                          color: isSel ? 'white' : 'rgba(15,42,74,0.6)',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: '"Montserrat", sans-serif',
                          boxShadow: '0 2px 6px rgba(15,42,74,0.03)'
                        }}
                      >
                        {t === 'GENERAL' ? 'General' : 'Por campaña'}
                      </button>
                    );
                  })}
                </div>
              </div>
              {selectedBrand.tableType === 'CAMPAIGN' && (
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.5)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                    Submarcas / Líneas creativas
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <input
                      value={newSubBrand}
                      onChange={e => setNewSubBrand(e.target.value)}
                      placeholder="Nombre submarca..."
                      onKeyDown={e => e.key === 'Enter' && addSubBrand()}
                      className="liquid-glass-input"
                      style={{ flex: 1, padding: '8px 12px', borderRadius: '12px', fontSize: '12px', fontFamily: '"Montserrat", sans-serif', color: '#0f2a4a', outline: 'none' }}
                    />
                    <button
                      onClick={addSubBrand}
                      style={{ padding: '8px 14px', borderRadius: '12px', border: 'none', background: 'rgba(0, 112, 243, 0.95)', color: 'white', fontSize: '13px', fontWeight: 800, cursor: 'pointer', fontFamily: '"Montserrat", sans-serif' }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {subBrands.map((s: any) => (
                      <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255, 255, 255, 0.75)', backdropFilter: 'blur(8px)', borderRadius: '20px', padding: '4px 12px', fontSize: '11px', fontWeight: 700, color: '#0f2a4a', border: '1px solid rgba(255, 255, 255, 0.85)' }}>
                        {s.name}
                        <button onClick={() => deleteSubBrand(s.id)} style={{ background: 'none', border: 'none', color: 'rgba(15,42,74,0.4)', cursor: 'pointer', fontSize: '13px', lineHeight: '1', padding: 0 }}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Buscador y Filtros por Línea Creativa */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: '12px', color: '#64748b', display: 'flex', alignItems: 'center' }}>
              <IconSearch size={14} />
            </span>
            <input
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Buscar por cuadro o campaña..."
              className="liquid-glass-input"
              style={{ padding: '8px 14px 8px 32px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, fontFamily: '"Montserrat", sans-serif', color: '#0f2a4a', outline: 'none', width: '280px' }}
            />
            {filterText && (
              <button onClick={() => setFilterText('')} style={{ position: 'absolute', right: '10px', border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '14px' }}>✕</button>
            )}
          </div>

          {isCampaign && subBrands.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setFilterSub('')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '12px',
                  border: !filterSub ? '1px solid rgba(255,255,255,0.7)' : '1px solid rgba(255,255,255,0.8)',
                  background: !filterSub ? 'linear-gradient(135deg, rgba(15, 42, 74, 0.95), rgba(15, 42, 74, 0.85))' : 'rgba(255, 255, 255, 0.55)',
                  color: !filterSub ? 'white' : 'rgba(15,42,74,0.6)',
                  fontSize: '11px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif'
                }}
              >
                Todas
              </button>
              {subBrands.map((s: any) => {
                const isSel = filterSub === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setFilterSub(s.id)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '12px',
                      border: isSel ? '1px solid rgba(255,255,255,0.7)' : '1px solid rgba(255,255,255,0.8)',
                      background: isSel ? 'linear-gradient(135deg, rgba(0, 112, 243, 0.95), rgba(0, 81, 179, 0.9))' : 'rgba(255, 255, 255, 0.55)',
                      color: isSel ? 'white' : 'rgba(15,42,74,0.6)',
                      fontSize: '11px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── KPI Cards de Diapositivas Reales (Estilo Dashboard Glassmorphism en Azul Oscuro con Roulette Numbers) ── */}
        <div className="zoik-mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '18px', marginBottom: '22px', flexShrink: 0 }}>
          
          {/* KPI 1: Total Diapositivas / Diseños */}
          <div
            className="liquid-glass-card"
            style={{ borderRadius: '20px', padding: '22px 24px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Total Diapositivas / Diseños
              </span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconLayers size={18} color="#0f2a4a" />
              </div>
            </div>
            <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={totalSlides} />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '8px' }}>
              {filteredPieces.length} fichas/carpetas registradas
            </div>
          </div>

          {/* KPI 2: Diapositivas Aprobadas / Entregadas */}
          <div
            className="liquid-glass-card"
            style={{ borderRadius: '20px', padding: '22px 24px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Diapositivas Aprobadas / Entregadas
              </span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCheckCircle size={18} color="#0f2a4a" />
              </div>
            </div>
            <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={approvedSlides} />
            </div>
            <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a' }}></span>
              Listas y contabilizadas
            </div>
          </div>

          {/* KPI 3: Pendientes de Aprobación */}
          <div
            className="liquid-glass-card"
            style={{ borderRadius: '20px', padding: '22px 24px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Pendientes de Aprobación
              </span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconClock size={18} color="#0f2a4a" />
              </div>
            </div>
            <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={pendingSlides} />
            </div>
            <div style={{ fontSize: '11px', color: '#ea580c', fontWeight: 700, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#f59e0b' }}></span>
              En revisión interna / cliente
            </div>
          </div>
        </div>

        {/* ── Tabla de Producción en Liquid Glassmorphism ── */}
        <div className="liquid-glass-panel zoik-touch-scroll" style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', borderRadius: '18px', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: 'rgba(15,42,74,0.3)', fontSize: '13px', fontWeight: 600 }}>
              Cargando piezas de producción...
            </div>
          ) : (
            <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>#</th>
                  {isCampaign ? (
                    <>
                      <th style={thStyle}>Asignación</th>
                      <th style={thStyle}>Tipo de pieza</th>
                      <th style={thStyle}>Línea creativa</th>
                      <th style={thStyle}>Fecha ejecución</th>
                      <th style={thStyle}>Fecha límite</th>
                      <th style={thStyle}>Estado</th>
                      <th style={{ ...thStyle, textAlign: 'center', width: '80px' }}>Rondas</th>
                      <th style={thStyle}>Cuadro / Campaña</th>
                    </>
                  ) : (
                    <>
                      <th style={thStyle}>Pieza</th>
                      <th style={thStyle}>Tipo de pieza</th>
                      <th style={thStyle}>Fecha creación</th>
                      <th style={thStyle}>Fecha publicación</th>
                      <th style={thStyle}>Tipo de contenido</th>
                      <th style={thStyle}>Estado</th>
                    </>
                  )}
                  <th style={{ ...thStyle, textAlign: 'center', width: '64px' }}>Hist.</th>
                  {user?.role !== 'CLIENTE' && <th style={{ ...thStyle, width: '40px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {pieces.length === 0 && (
                  <tr>
                    <td colSpan={isCampaign ? 10 : 8} style={{ padding: '50px', textAlign: 'center', color: 'rgba(15,42,74,0.3)', fontSize: '13px', fontStyle: 'italic', fontWeight: 600 }}>
                      Sin piezas registradas en este período
                    </td>
                  </tr>
                )}
                {pieces.map((p, i) => {
                  const rowBg = i % 2 === 0 ? 'rgba(255, 255, 255, 0.45)' : 'rgba(248, 250, 252, 0.25)';
                  const statusColor = STATUS_COLORS[p.status] || '#64748b';
                  const statusLabel = STATUS_OPTS.find(s => s.v === p.status)?.l || p.status;
                  const subLabel = subBrands.find((s: any) => s.id === p.subBrandId)?.name || '—';
                  const typeLabel = TYPE_OPTS.find(t => t.v === p.type)?.l || p.type || '—';
                  const ctLabel = CONTENT_OPTS.find(c => c.v === p.contentType)?.l || '—';

                  return (
                    <tr
                      key={p.id}
                      style={{ background: rowBg, transition: 'background 0.15s ease' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0, 112, 243, 0.06)')}
                      onMouseLeave={e => (e.currentTarget.style.background = rowBg)}
                    >
                      <td style={{ ...tdStyle, textAlign: 'center', color: 'rgba(15,42,74,0.4)', fontWeight: 800, width: '40px' }}>
                        {i + 1}
                      </td>
                      {isCampaign ? (
                        <>
                          <CellEdit pieceId={p.id} field="title" value={p.title}>
                            <span style={{ fontWeight: 700, color: '#0f2a4a' }}>{p.title || '—'}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="type" value={p.type}>
                            <span style={{ fontWeight: 600 }}>{typeLabel}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="subBrandId" value={p.subBrandId}>
                            <span style={{ color: p.subBrandId ? '#0f2a4a' : 'rgba(15,42,74,0.3)', fontWeight: 600 }}>{subLabel}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="executionDate" value={p.executionDate}>
                            <span>{fmtDate(p.executionDate)}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="dueDate" value={p.dueDate || p.request?.dueDate}>
                            <span>{fmtDate(p.dueDate || p.request?.dueDate)}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="status" value={p.status}>
                            <span style={{
                              background: `${statusColor}18`,
                              color: statusColor,
                              border: `1px solid ${statusColor}35`,
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 800,
                              backdropFilter: 'blur(6px)'
                            }}>
                              {statusLabel}
                            </span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="changeRounds" value={p.changeRounds}>
                            <span style={{ display: 'block', textAlign: 'center', fontWeight: 800, color: p.changeRounds > 0 ? '#f59e0b' : 'rgba(15,42,74,0.3)' }}>
                              {p.changeRounds || 0}
                            </span>
                          </CellEdit>
                          <td style={tdStyle}>
                            {p.materialsBoardId ? (
                              <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', background: 'rgba(0, 112, 243, 0.1)', color: '#0070f3', fontWeight: 700 }}>
                                {materialsBoards.find((b: any) => b.id === p.materialsBoardId)?.name || '—'}
                              </span>
                            ) : (
                              <span style={{ color: 'rgba(15,42,74,0.25)' }}>—</span>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          <CellEdit pieceId={p.id} field="title" value={p.title}>
                            <span style={{ fontWeight: 700, color: '#0f2a4a' }}>{p.title || '—'}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="type" value={p.type}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                              {typeLabel}
                              {p.type === 'CARRUSEL' && (
                                <span style={{ fontSize: '10px', fontWeight: 800, background: 'rgba(0, 112, 243, 0.1)', color: '#0070f3', padding: '2px 6px', borderRadius: '6px' }}>
                                  {getPieceSlides(p)} diaps
                                </span>
                              )}
                              {isVideoX3Brand(selectedBrand?.name) && ['VIDEO', 'ANIMACION', 'REEL'].includes(p.type) && (
                                <span style={{ fontSize: '10px', fontWeight: 800, background: 'rgba(245, 158, 11, 0.12)', color: '#d97706', padding: '2px 6px', borderRadius: '6px' }}>
                                  x3
                                </span>
                              )}
                            </span>
                          </CellEdit>
                          <td style={tdStyle}>{fmtDate(p.createdAt)}</td>
                          <CellEdit pieceId={p.id} field="publishDate" value={p.publishDate}>
                            <span>{fmtDate(p.publishDate)}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="contentType" value={p.contentType}>
                            <span style={{ color: p.contentType ? '#0f2a4a' : 'rgba(15,42,74,0.25)', fontWeight: 600 }}>{ctLabel}</span>
                          </CellEdit>
                          <CellEdit pieceId={p.id} field="status" value={p.status}>
                            <span style={{
                              background: `${statusColor}18`,
                              color: statusColor,
                              border: `1px solid ${statusColor}35`,
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 800,
                              backdropFilter: 'blur(6px)'
                            }}>
                              {statusLabel}
                            </span>
                          </CellEdit>
                        </>
                      )}
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button
                          onClick={() => openHistory(p)}
                          title="Ver historial"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: 'rgba(15,42,74,0.35)',
                            padding: '4px',
                            transition: 'color 0.15s ease'
                          }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#0070f3')}
                          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(15,42,74,0.35)')}
                        >
                          🕐
                        </button>
                      </td>
                      {user?.role !== 'CLIENTE' && (
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button
                            onClick={() => deleteRow(p.id)}
                            title="Eliminar fila"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: 'rgba(15,42,74,0.25)',
                              padding: '4px',
                              transition: 'color 0.15s ease'
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(15,42,74,0.25)')}
                          >
                            ✕
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {pieces.length > 0 && (() => {
                const bBilling = calculateBrandBilling(selectedBrand?.name || '', totalSlides);
                return (
                  <tfoot>
                    <tr style={{
                      background: 'linear-gradient(135deg, rgba(240, 253, 244, 0.85), rgba(220, 252, 231, 0.75))',
                      backdropFilter: 'blur(12px)',
                      borderTop: '2px solid rgba(16, 185, 129, 0.4)'
                    }}>
                      <td colSpan={isCampaign ? 3 : 3} style={{ ...tdStyle, fontWeight: 900, color: '#166534', fontSize: '13px' }}>
                        TOTAL: {totalSlides} unidades / diapositivas ({filteredPieces.length} fichas)
                      </td>
                      <td colSpan={isCampaign ? 5 : 3} style={{ ...tdStyle, fontWeight: 700, color: '#15803d', fontSize: '12px' }}>
                        Aprobadas: {approvedSlides} unidades · Liquidación: ${bBilling.total.toLocaleString('es-CO')} COP ({bBilling.planName}{bBilling.extraUnits > 0 ? ` + ${bBilling.extraUnits} extras: $${bBilling.extraAmount.toLocaleString('es-CO')}` : ''})
                      </td>
                      <td colSpan={2} style={tdStyle}></td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          )}

          {selectedBrand && user?.role !== 'CLIENTE' && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(15,42,74,0.06)', background: 'rgba(255, 255, 255, 0.4)' }}>
              <button
                onClick={addRow}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  borderRadius: '12px',
                  border: '1.5px dashed rgba(0, 112, 243, 0.35)',
                  background: 'rgba(0, 112, 243, 0.04)',
                  color: '#0070f3',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(0, 112, 243, 0.08)';
                  e.currentTarget.style.borderColor = '#0070f3';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(0, 112, 243, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(0, 112, 243, 0.35)';
                }}
              >
                + Nueva fila
              </button>
            </div>
          )}
        </div>

        {/* Panel Lateral de Historial en Liquid Glass */}
        {historyPiece && (
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '380px',
            background: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(28px)',
            WebkitBackdropFilter: 'blur(28px)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.9)',
            boxShadow: '-8px 0 32px rgba(15, 42, 74, 0.12)',
            zIndex: 200,
            display: 'flex',
            flexDirection: 'column',
            animation: 'glassDeploy 0.24s cubic-bezier(0.16, 1, 0.3, 1) forwards'
          }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(15,42,74,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 900, color: '#0f2a4a' }}>Historial de cambios</div>
                <div style={{ fontSize: '12px', color: 'rgba(15,42,74,0.45)', marginTop: '2px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {historyPiece.title}
                </div>
              </div>
              <button
                onClick={() => setHistoryPiece(null)}
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  border: '1px solid rgba(15,42,74,0.1)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  cursor: 'pointer',
                  fontSize: '16px',
                  lineHeight: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {history.length === 0 && (
                <div style={{ color: 'rgba(15,42,74,0.3)', fontSize: '13px', padding: '30px 0', textAlign: 'center', fontWeight: 600 }}>
                  Sin cambios registrados
                </div>
              )}
              {history.map(h => (
                <div key={h.id} style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid rgba(15,42,74,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#0070f3' }}>{h.user?.firstName} {h.user?.lastName}</span>
                    <span style={{ fontSize: '11px', color: 'rgba(15,42,74,0.35)', fontWeight: 600 }}>{new Date(h.createdAt).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(15,42,74,0.6)', marginBottom: '4px', fontWeight: 600 }}>
                    Campo: <strong style={{ color: '#0f2a4a' }}>{FIELD_LABELS[h.field] || h.field}</strong>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px' }}>
                    <span style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444', padding: '3px 8px', borderRadius: '8px', textDecoration: 'line-through', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{h.oldValue || '—'}</span>
                    <span style={{ color: 'rgba(15,42,74,0.3)' }}>→</span>
                    <span style={{ background: 'rgba(16,185,129,0.08)', color: '#10b981', padding: '3px 8px', borderRadius: '8px', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700 }}>{h.newValue || '—'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* ── MODAL DE INFORME GENERAL DE PAGOS Y LIQUIDACIÓN (LIQUID GLASS) ── */}
      {showBillingModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 42, 74, 0.45)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '24px'
        }}>
          <div
            className="liquid-glass-modal"
            style={{
              borderRadius: '24px',
              padding: '30px 36px',
              maxWidth: '1050px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px', borderBottom: '1px solid rgba(15,42,74,0.08)', paddingBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <AnimatedBarChartIcon size={22} />
                  <span>Informe General de Pagos y Liquidación ({MONTHS[month - 1]} {year})</span>
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Consolidado financiero por marca según unidades reales y acuerdos comerciales.
                </p>
              </div>
              <button
                onClick={() => setShowBillingModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '1px solid rgba(15,42,74,0.1)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {loadingBilling ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
                Calculando liquidación financiera...
              </div>
            ) : !billingData ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
                No se pudo cargar el informe.
              </div>
            ) : (
              <div>
                {/* Gran Total Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(15, 42, 74, 0.95), rgba(0, 112, 243, 0.9))',
                  backdropFilter: 'blur(16px)',
                  borderRadius: '18px',
                  padding: '22px 28px',
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '24px',
                  boxShadow: '0 8px 24px rgba(15, 42, 74, 0.15), inset 0 1.5px 2px rgba(255, 255, 255, 0.4)'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 800, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      Total a Facturar Agencia ({MONTHS[month - 1]} {year})
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: 900, marginTop: '4px', letterSpacing: '-1px' }}>
                      ${(billingData.grandTotalAmount || 0).toLocaleString('es-CO')} COP
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', fontWeight: 800, opacity: 0.85, textTransform: 'uppercase' }}>
                      Total Unidades Producidas
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 900, marginTop: '4px' }}>
                      {billingData.grandTotalUnits || 0} diapositivas/piezas
                    </div>
                  </div>
                </div>

                {/* Tabla de liquidación por marca */}
                <div className="liquid-glass-panel" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: 'rgba(15, 42, 74, 0.06)', borderBottom: '2px solid rgba(15, 42, 74, 0.1)' }}>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a' }}>Marca</th>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a', textAlign: 'center' }}>Fichas</th>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a', textAlign: 'center' }}>Unidades (Diaps)</th>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a', textAlign: 'center' }}>Aprobadas</th>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a' }}>Plan / Acuerdo</th>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a', textAlign: 'right' }}>Fee Base</th>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a', textAlign: 'right' }}>Extras</th>
                        <th style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a', textAlign: 'right' }}>Total a Cobrar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingData.brands?.map((b: any, idx: number) => {
                        const f = b.financial;
                        return (
                          <tr key={b.brandId} style={{ borderBottom: '1px solid rgba(15,42,74,0.06)', background: idx % 2 === 0 ? 'rgba(255,255,255,0.45)' : 'rgba(248,250,252,0.25)' }}>
                            <td style={{ padding: '12px', fontWeight: 800, color: '#0f2a4a' }}>
                              {b.brandName}
                              {b.isX3 && <span style={{ marginLeft: '6px', fontSize: '10px', background: 'rgba(245,158,11,0.15)', color: '#d97706', padding: '2px 6px', borderRadius: '6px', fontWeight: 800 }}>Videos x3</span>}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>{b.totalPieces}</td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: 900, color: '#0f2a4a' }}>{b.totalUnits}</td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: 800, color: '#10b981' }}>{b.approvedUnits}</td>
                            <td style={{ padding: '12px', color: '#334155', fontWeight: 600 }}>{f.planName}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700 }}>${(f.feeBase || 0).toLocaleString('es-CO')}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: f.extraUnits > 0 ? '#d97706' : '#64748b', fontWeight: 700 }}>
                              {f.extraUnits > 0 ? `+${f.extraUnits} ($${f.extraAmount.toLocaleString('es-CO')})` : '—'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontWeight: 900, color: '#0f2a4a', fontSize: '13px' }}>
                              ${(f.totalAmount || 0).toLocaleString('es-CO')} COP
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', gap: '12px' }}>
                  <button
                    onClick={handlePrintFormalPDF}
                    style={{
                      padding: '11px 24px',
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 112, 243, 0.3)',
                      background: 'linear-gradient(135deg, #0070f3, #005bb5)',
                      color: 'white',
                      fontWeight: 800,
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 14px rgba(0, 112, 243, 0.25)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 112, 243, 0.45)')}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 112, 243, 0.25)')}
                  >
                    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 6 2 18 2 18 9" />
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    <span>Generar PDF Formal Oficial</span>
                  </button>
                  <button
                    onClick={() => setShowBillingModal(false)}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, rgba(15, 42, 74, 0.95), rgba(15, 42, 74, 0.85))',
                      color: 'white',
                      fontWeight: 800,
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Production;
